import { inject,
  Component, ViewEncapsulation, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EventsService } from '@services/events/events.service';
import { StorageService } from '@services/storage/storage.service';
import { VariablesService } from '@services/variables/variables.service';
import { VwoService } from '@services/vwo/vwo.service';
import { ICart, IContribute, IPaymentConfig } from '@models/payment.model';
import { Subject, Subscription } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PaymentUtilService } from '@services/payment-util/payment-util.service';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { CustomTranslatePipe } from '@pipes/translate/translate.pipe';
import { CurrencyIconComponent } from '@core-components/currency-icon/currency-icon.component';
import { CurrencyDropdownComponent } from '../../../currency-dropdown/currency-dropdown.component';

@Component({
  selector: 'app-slabs',
  templateUrl: './slabs.component.html',
  styleUrls: ['./slabs.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    AsyncPipe,
    ReactiveFormsModule,
    MatButtonModule,
    CustomTranslatePipe,
    CurrencyIconComponent,
    CurrencyDropdownComponent
  ]
})
export class SlabsComponent {

  private actiRoute = inject(ActivatedRoute);
  private storage = inject(StorageService);
  private paymentUtil = inject(PaymentUtilService);
  private events = inject(EventsService);
  public vars = inject(VariablesService);
  public vwo = inject(VwoService);

  @Input() currency;
  @Input() params;
  @Input() reward;
  @Input() isReward;
  @Input() type;
  @Input() setOtherAmount: Subject<any>;
  @Input() contributeDetails: IContribute;
  @Input() checkForOtherCases = true;
  @Input() showSlabNameTag;
  @Input() recommendTag;
  @Input() payment_config: IPaymentConfig;
  @Input() tagLabel;
  @Input() theme;
  @Input() isQR;
  @Output() userSelectionCurrency: EventEmitter<any> = new EventEmitter();
  @Output() amountChange: EventEmitter<any> = new EventEmitter();
  slabs = [];
  amountForm: FormGroup;
  amount: FormControl;
  oldAmountValue: number = 0;
  otherAmountLabel = 'Other Amount';
  @ViewChild('otherAmountBtnRef') public otherAmountBtnRef;
  @ViewChild('otherAmountRef') public otherAmountRef: ElementRef<HTMLElement>;
  @ViewChild('amountRef') public amountRef: ElementRef<HTMLInputElement>;
  otherAmountSelected = false;
  language;

  max_donation = 50000000;

  subs: Subscription[] = [];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['contributeDetails'] && !changes['contributeDetails']['firstChange']) {
      this.updateMinValidation();
      this.slabConditions();
    }
  }
  ngOnInit() {
    this.vars.ShowIntentWithMinAmount.subscribe((res) => {
      if (res) {
        this.otherAmountAnimate();
        this.amount.setValue(this.contributeDetails.min_donation);
        this.amountRef.nativeElement.focus();
      }
    });
    this.vars.languageObs.subscribe((currentlang) => {
      this.language = currentlang;
    });
    if (this.isQR?.name === 'UPI') {
      this.max_donation = 100000
    }
    this.setConfig();
    this.amountForm = new FormGroup({
      amount: this.amount = new FormControl('', [
        Validators.required, Validators.pattern(/^[0-9]+$/), Validators.min(this.contributeDetails.min_donation), Validators.max(this.max_donation)
      ]),
    });
    this.slabConditions();
    this.paymentUtil.selectedReward.subscribe({
      next: (res) => {
        if (res) {
          this.reward = res;
          this.amount.setValue(this.reward.amount);
        }
      }
    });
    this.amount.valueChanges.subscribe((res) => {
      this.amountChange.next(res || 0)
    });
    if (this.vars.showKCart) {
      this.subs.push(this.vars.kKartSelectionChange.subscribe(data => {
        if (this.vars.selectedKCart) {
          this.setSlabs(this.contributeDetails.slabs, data?.amount || 0);
        } else {
          this.slabConditions();
        }
      }));
    }
  }

  setConfig() {
    this.subs.push(this.paymentUtil.paymentConfig.subscribe((c) => {
      if (c) {
        this.payment_config = { ...this.payment_config, ...c };
      }
    }));
  }

  ngAfterViewInit() {
    if (this.isReward) {
      setTimeout(() => {
        this.otherAmountAnimate();
        this.amountRef.nativeElement.focus();
      }, 0);
    }
  }

  // Update min validation on currency change
  updateMinValidation() {
    this.amount.setValidators([
      Validators.required, Validators.pattern(/^[0-9]+$/), Validators.min(this.contributeDetails.min_donation), Validators.max(this.max_donation)
    ]);
  }

  /**Slab conditions */
  slabConditions() {
    if (this.params.st === 'f') {
      const cartData = this.storage.getFromSession('cartData');
      const tipAmount = cartData?.tip_amount ? +cartData.tip_amount : 0;
      if (cartData?.bulkdonation) {
        Object.keys(cartData?.bulkdonation).forEach(id => {
          if (this.vars.fundraiser.id === +id && cartData?.bulkdonation?.[id]?.amount) {
            cartData.donated_amount = +cartData.bulkdonation[id].amount;
          };
        });
      }
      this.setSlabs(this.contributeDetails.slabs, cartData.donated_amount - tipAmount);
    } else if (this.isReward) {
      this.amount.setValue(this.reward ? this.reward.amount : this.contributeDetails.default_amount);
      this.amountChange.next(this.reward ? this.reward.amount : this.contributeDetails.default_amount);
    } else {
      this.amount.setValue(this.contributeDetails.default_amount);
      this.amountChange.next(this.contributeDetails.default_amount);
      this.setSlabs(this.contributeDetails.slabs, this.contributeDetails.default_amount);
    }
  }
  /**set the slabs */
  setSlabs(slabs: any[], defaultAmount) {
    if (defaultAmount < this.contributeDetails.min_donation) { defaultAmount = this.contributeDetails.default_amount; }
    this.slabs = [];
    const qParams = this.actiRoute.snapshot.queryParams;
    this.amount.setValidators([
      Validators.required, Validators.pattern(/^[0-9]+$/), Validators.min(this.contributeDetails.min_donation), Validators.max(this.max_donation)
    ]);
    // adding 100Rs on activating 100rs coupon when utm_source=wallet_offer_100 popup is clicked
    const setAmount = { amount: this.paymentUtil.utmProceedClicked ? defaultAmount + 100 : defaultAmount, isSelected: false };
    for (const slab of slabs) {
      // adding 100Rs on activating 100rs coupon when utm_source=wallet_offer_100 popup is clicked
      const slabData = {
        amount: this.paymentUtil.utmProceedClicked ? slab + 100 : slab,
        isSelected: slab === defaultAmount ? true : false
      };
      this.slabs.push(slabData);
    }
    // this.slabs[0].name_tag = 'POCKET FIRENDLY';
    // this.slabs[1].name_tag = 'MOST POPULAR';
    // this.slabs[2].name_tag = 'COMMITTED DONOR';

    if (this.checkForOtherCases) {
      if (qParams.donated_amount) {
        this.setAmountIfFailed(slabs, +qParams.donated_amount, { amount: +qParams.donated_amount, isSelected: false });
      } else if (qParams.abandon) {
        this.setAmountIfFailed(slabs, +qParams.amount, { amount: +qParams.amount, isSelected: false });
      } else if (this.params.st === 'f' && this.storage.getFromSession('cartData')) {
        const cart: ICart = this.storage.getFromSession('cartData');
        const tipAmount = cart?.tip_amount ? +cart.tip_amount : 0;
        if (cart?.bulkdonation) {
          Object.keys(cart?.bulkdonation).forEach(id => {
            if (this.vars.fundraiser.id === +id && cart?.bulkdonation?.[id]?.amount) {
              cart.donated_amount = +cart.bulkdonation[id].amount;
            };
          });
        }
        const donatedAmt = cart.donated_amount - tipAmount;
        this.setAmountIfFailed(slabs, donatedAmt, setAmount);
      } else {
        this.setAmount(setAmount);
      }
    } else {
      this.setAmount(setAmount);
    }
  }

  /**If tansaction is failed then set the failed amount */
  setAmountIfFailed(slabs, donated_amount, setAmount) {
    this.setAmount(setAmount);
    if (slabs?.map(Number)?.indexOf(donated_amount) !== -1) {
      for (const slab of this.slabs) {
        slab.isSelected = +slab.amount === donated_amount ? true : false;
      }
    } else {
      this.otherAmountAnimate();
    }
  }

  /**Set the amount to donate */
  setAmount(slab) {
    for (const item of this.slabs) {
      item.isSelected = item.amount === slab.amount ? true : false;
    }
    this.amount.setValue(slab.amount);
    this.amountChange.next(slab.amount);
  }

  /**Animated the other amount button, by clicking on it */
  otherAmountAnimate() {
    for (const slab of this.slabs) {
      slab.isSelected = false;
    }
    if (this.otherAmountBtnRef) {
      this.otherAmountBtnRef._elementRef.nativeElement.style.width = '100%';
      this.otherAmountSelected = true;
      this.otherAmountLabel = '';
      this.otherAmountRef.nativeElement.style.display = 'unset';
    }
    const sessionKart = this.storage.getFromSession('cartData');
    if (sessionKart?.campaign_products) {
      this.vars.resetSelectedKCart.next(sessionKart.campaign_products);
    }
  }

  // onblur trigger a event
  otherAmountBlur(data: string) {
    const amount = +data;
    
    if (this.vars.fundraiser?.cause?.parent_cause_id !== 149 || amount <= 0 || amount === this.oldAmountValue) return;

    this.oldAmountValue = amount;

    const ePayload = {
      eventName: 'Amount less than limit',
      info_1: amount
    };
    this.events.sendSystemEvent(ePayload).subscribe(_ => _);
  }

  /**Set the user selected currency */
  setCurrency(data) {
    this.userSelectionCurrency.emit(data);
  }

  ngOnDestroy() {
    this.oldAmountValue = 0;
    this.subs.forEach(s => s.unsubscribe());
  }

}
